/**
 * Logical clip catalog + AudioEvent → clip id resolution.
 * Paths are under public/audio/{bus}/{file} without extension (shipped as .mp3).
 */

import type { AudioEvent } from "./events";
import type { AudioChildBusId } from "./settings";

/** Catalog id e.g. `sfx.answer.meet` */
export type ClipId = string;

export interface CatalogEntry {
  id: ClipId;
  bus: AudioChildBusId;
  /** Relative path under /audio/ without extension: `sfx/answer-meet` */
  path: string;
  /** Optional gain scale when playing (default 1) */
  gain?: number;
  loop?: boolean;
}

function entry(
  id: ClipId,
  bus: AudioChildBusId,
  path: string,
  extra?: Pick<CatalogEntry, "gain" | "loop">,
): CatalogEntry {
  return { id, bus, path, ...extra };
}

/**
 * MVP + vertical-slice catalog. Missing files are silent in prod / synth in DEV.
 * Naming: `sfx.answer.meet` → `sfx/answer-meet`
 */
export const CATALOG: readonly CatalogEntry[] = [
  // Combat SFX
  entry("sfx.answer.meet", "sfx", "sfx/answer-meet"),
  entry("sfx.answer.weak", "sfx", "sfx/answer-weak"),
  entry("sfx.answer.miss", "sfx", "sfx/answer-miss"),
  entry("sfx.answer.blind-hit", "sfx", "sfx/answer-blind-hit"),
  entry("sfx.answer.target", "sfx", "sfx/answer-target", { gain: 0.7 }),
  entry("sfx.still", "sfx", "sfx/still"),
  entry("sfx.silence", "sfx", "sfx/silence"),
  entry("sfx.breach", "sfx", "sfx/breach"),
  entry("sfx.press", "sfx", "sfx/press", { gain: 0.65 }),
  entry("sfx.overflow", "sfx", "sfx/overflow"),
  entry("sfx.spawn-fear", "sfx", "sfx/spawn-fear"),
  entry("sfx.spawn-despair", "sfx", "sfx/spawn-despair"),
  entry("sfx.spawn-generic", "sfx", "sfx/spawn-generic", { gain: 0.8 }),
  entry("sfx.multiply", "sfx", "sfx/multiply", { gain: 0.75 }),
  entry("sfx.redeem", "sfx", "sfx/redeem"),
  entry("sfx.abide", "sfx", "sfx/abide"),
  entry("sfx.layer", "sfx", "sfx/layer", { gain: 0.75 }),
  entry("sfx.restore", "sfx", "sfx/restore", { gain: 0.7 }),
  entry("sfx.proclaim-start", "sfx", "sfx/proclaim-start"),
  entry("sfx.proclaim-end", "sfx", "sfx/proclaim-end", { gain: 0.85 }),
  /** Soft category seal under proclaim start (Faith / Peace starter palette) */
  entry("sfx.seal-faith", "sfx", "sfx/seal-faith", { gain: 0.45 }),
  entry("sfx.seal-peace", "sfx", "sfx/seal-peace", { gain: 0.45 }),
  entry("sfx.seal-generic", "sfx", "sfx/seal-generic", { gain: 0.4 }),

  // UI
  entry("ui.click", "ui", "ui/click", { gain: 0.6 }),
  entry("ui.deny", "ui", "ui/deny", { gain: 0.7 }),
  entry("ui.pause", "ui", "ui/pause", { gain: 0.55 }),
  entry("ui.queue", "ui", "ui/queue", { gain: 0.55 }),
  entry("ui.queue-cancel", "ui", "ui/queue-cancel", { gain: 0.5 }),
  entry("ui.reveal", "ui", "ui/reveal", { gain: 0.45 }),

  // Stingers
  entry("sting.start", "sfx", "sting/start"),
  entry("sting.win", "sfx", "sting/win"),
  entry("sting.lose", "sfx", "sting/lose"),
  entry("sting.near-miss", "sfx", "sting/near-miss"),

  // State (for later PRs; catalog ready)
  entry("state.drone", "state", "state/drone", { loop: true, gain: 0.5 }),
  entry("state.pulse-thump", "state", "state/pulse-thump", { gain: 0.85 }),
  entry("state.tilt-truth", "state", "state/tilt-truth", {
    loop: true,
    gain: 0.4,
  }),
  entry("state.tilt-lie", "state", "state/tilt-lie", { loop: true, gain: 0.4 }),
  entry("state.will-bleed", "state", "state/will-bleed", {
    loop: true,
    gain: 0.45,
  }),
  entry("state.proclaim-bed", "state", "state/proclaim-bed", {
    loop: true,
    gain: 0.35,
  }),

  // Environmental music — mountain ascent by integrity (MusicDirector crossfade)
  // foundation = forest (ease), ambient.secret = cliffside (medium), pressure = volcano (critical)
  entry("music.foundation", "music", "music/foundation", {
    loop: true,
    gain: 0.75,
  }),
  entry("music.pressure", "music", "music/pressure", { loop: true, gain: 0.75 }),
  entry("ambient.secret", "ambient", "ambient/secret", {
    loop: true,
    gain: 0.7,
  }),
] as const;

const BY_ID = new Map<ClipId, CatalogEntry>(
  CATALOG.map((e) => [e.id, e]),
);

export function getCatalogEntry(id: ClipId): CatalogEntry | undefined {
  return BY_ID.get(id);
}

export function allCatalogIds(): ClipId[] {
  return CATALOG.map((e) => e.id);
}

/** Continuous state beds (PR 4b). */
export const PRELOAD_STATE_IDS: readonly ClipId[] = [
  "state.drone",
  "state.pulse-thump",
  "state.tilt-truth",
  "state.tilt-lie",
  "state.will-bleed",
  "state.proclaim-bed",
] as const;

/** Environmental music beds (foundation required for MusicDirector start). */
export const PRELOAD_MUSIC_IDS: readonly ClipId[] = [
  "music.foundation",
  "music.pressure",
  "ambient.secret",
] as const;

/** Boot / mission preload tiers for Proclaim MVP one-shots. */
export const PRELOAD_COMBAT_IDS: readonly ClipId[] = [
  "sfx.answer.meet",
  "sfx.answer.weak",
  "sfx.answer.miss",
  "sfx.answer.blind-hit",
  "sfx.answer.target",
  "sfx.still",
  "sfx.silence",
  "sfx.breach",
  "sfx.press",
  "sfx.overflow",
  "sfx.spawn-fear",
  "sfx.spawn-despair",
  "sfx.spawn-generic",
  "sfx.multiply",
  "sfx.redeem",
  "sfx.abide",
  "sfx.layer",
  "sfx.restore",
  "sfx.proclaim-start",
  "sfx.proclaim-end",
  "ui.click",
  "ui.deny",
  "ui.pause",
  "ui.queue",
  "ui.queue-cancel",
  "ui.reveal",
  "sting.start",
  "sting.win",
  "sting.lose",
  "sting.near-miss",
] as const;

/**
 * Map structured answer fields → outcome clip.
 * Do not hard-code "steps ≥ 2 means super-effective" — use meet.
 */
export function resolveAnswerClipId(fields: {
  steps: number;
  meet: boolean;
}): ClipId {
  const { steps, meet } = fields;
  if (meet) return "sfx.answer.meet";
  if (steps <= 0) return "sfx.answer.miss";
  return "sfx.answer.weak";
}

function spawnClipForType(lieType: string): ClipId {
  const t = lieType.toLowerCase();
  if (t === "fear") return "sfx.spawn-fear";
  if (t === "despair") return "sfx.spawn-despair";
  return "sfx.spawn-generic";
}

function categorySealClip(category: string): ClipId {
  const c = category.toLowerCase();
  if (c === "faith") return "sfx.seal-faith";
  if (c === "peace") return "sfx.seal-peace";
  return "sfx.seal-generic";
}

/**
 * Minimal audio preset: UI + major stingers + decisive combat only.
 * No spawn spam, press grit, weak hits, or category fluff.
 */
export function clipAllowedInMinimal(id: ClipId): boolean {
  if (id.startsWith("sting.")) return true;
  if (id.startsWith("ui.")) return true;
  switch (id) {
    case "sfx.answer.meet":
    case "sfx.answer.miss":
    case "sfx.silence":
    case "sfx.breach":
    case "sfx.overflow":
    case "sfx.redeem":
    case "sfx.still":
    case "sfx.proclaim-start":
    case "sfx.proclaim-end":
      return true;
    default:
      return false;
  }
}

/** One-shot rate limits (seconds) to reduce SFX fatigue under board pressure. */
export const CLIP_COOLDOWN_S: Readonly<Partial<Record<ClipId, number>>> = {
  "sfx.spawn-fear": 0.14,
  "sfx.spawn-despair": 0.14,
  "sfx.spawn-generic": 0.14,
  "sfx.multiply": 0.18,
  "sfx.press": 0.22,
  "sfx.overflow": 0.32,
  "ui.reveal": 0.15,
};

/**
 * Resolve an AudioEvent to zero or more catalog clip ids (play order).
 * Pure — no audio nodes.
 */
export function resolveEventClips(e: AudioEvent): ClipId[] {
  switch (e.type) {
    case "ui.click":
      return ["ui.click"];
    case "ui.illegal":
      return ["ui.deny"];
    case "ui.queue":
      return ["ui.queue"];
    case "ui.queueCancel":
      return ["ui.queue-cancel"];
    case "mission.start":
      return ["sting.start"];
    case "mission.won":
      return e.nearMiss ? ["sting.near-miss"] : ["sting.win"];
    case "mission.lost":
      return ["sting.lose"];
    case "proclaim.start":
      return ["sfx.proclaim-start", categorySealClip(e.category)];
    case "proclaim.end":
      return ["sfx.proclaim-end"];
    case "answer.resolve": {
      const base = resolveAnswerClipId(e);
      if (e.correctTarget) return [base, "sfx.answer.target"];
      return [base];
    }
    case "instant.still":
      return ["sfx.still"];
    case "instant.layer":
      if (e.selector === "slowLayer") return ["sfx.still"];
      return ["sfx.layer"];
    case "instant.self":
      return ["sfx.restore"];
    case "placeable.abide":
      return ["sfx.abide"];
    case "redeem":
      return ["sfx.redeem"];
    case "lie.spawn":
      if (e.multiplied) return [spawnClipForType(e.lieType), "sfx.multiply"];
      return [spawnClipForType(e.lieType)];
    case "lie.degrade":
      // Claim VO only (handled by LieVoicePlayer); no SFX
      return [];
    case "lie.silence":
      return ["sfx.silence"];
    case "lie.breach":
      return ["sfx.breach"];
    case "lie.press":
      return ["sfx.press"];
    case "overflow.flash":
      return ["sfx.overflow"];
    case "reveal.open":
    case "reveal.close":
      return ["ui.reveal"];
    case "pause":
      return e.paused ? ["ui.pause"] : ["ui.click"];
    default: {
      // Exhaustiveness
      const _never: never = e;
      void _never;
      return [];
    }
  }
}
